import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FileDown, Lock, Mail, Play } from "lucide-react";
import { api } from "@/lib/api";
import VisionDeck from "./VisionDeck";
import "./vision.css";

const mono = '"JetBrains Mono", ui-monospace, monospace';
const switzer = '"Switzer", "Instrument Sans", sans-serif';

const feltStil = {
  width: "100%",
  background: "rgba(7, 10, 14, 0.6)",
  border: "1px solid rgba(245, 241, 232, 0.18)",
  color: "#f5f1e8",
  padding: "11px 14px",
  fontFamily: '"Instrument Sans", sans-serif',
  fontSize: 16,
  outline: "none",
};

const etikettStil = {
  fontFamily: mono,
  fontSize: 9,
  letterSpacing: "0.24em",
  textTransform: "uppercase",
  color: "#8b9199",
  display: "block",
  marginBottom: 6,
};

function KontaktModal({ token, onLukk }) {
  const [f, setF] = useState({ name: "", company: "", email: "", message: "" });
  const [sender, setSender] = useState(false);
  const [sendt, setSendt] = useState(false);
  const [feil, setFeil] = useState("");

  const send = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) return;
    setSender(true);
    setFeil("");
    try {
      await api.post(`/vision/s/${token}/kontakt`, f);
      setSendt(true);
    } catch {
      setFeil("Something went wrong — please try again or email martin@norwegianaisummit.no");
    } finally {
      setSender(false);
    }
  };

  const sett = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 90, background: "rgba(5, 7, 11, 0.8)", backdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      data-testid="vis-kontakt-modal"
      onClick={(e) => e.target === e.currentTarget && onLukk()}
    >
      <div style={{ width: "min(540px, 100%)", background: "#0a0e13", border: "1px solid rgba(245, 241, 232, 0.14)", padding: "clamp(26px, 4vw, 44px)", maxHeight: "90vh", overflowY: "auto" }}>
        {sendt ? (
          <div data-testid="vis-kontakt-takk">
            <p style={{ fontFamily: mono, fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", color: "#f1e75a" }}>Thank you</p>
            <h3 style={{ marginTop: 14, fontFamily: switzer, fontWeight: 500, textTransform: "uppercase", letterSpacing: "-0.015em", fontSize: "clamp(22px, 3vw, 32px)", lineHeight: 1.05, color: "#f5f1e8" }}>
              We'll be in touch.
            </h3>
            <p style={{ marginTop: 12, fontSize: 13.5, lineHeight: 1.65, color: "#8b9199" }}>
              Your message has been received. Expect to hear from us shortly.
            </p>
            <button type="button" className="vis-ctrl" style={{ marginTop: 22 }} onClick={onLukk} data-testid="vis-kontakt-lukk">
              Close
            </button>
          </div>
        ) : (
          <>
            <p style={{ fontFamily: mono, fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", color: "#f1e75a" }}>Start the conversation</p>
            <h3 style={{ marginTop: 14, fontFamily: switzer, fontWeight: 500, textTransform: "uppercase", letterSpacing: "-0.015em", fontSize: "clamp(22px, 3vw, 32px)", lineHeight: 1.05, color: "#f5f1e8" }}>
              Let's talk about <span style={{ color: "#f1e75a" }}>2027</span>.
            </h3>
            <form onSubmit={send} style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 16 }} data-testid="vis-kontakt-skjema">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14 }}>
                <div>
                  <label style={etikettStil}>Name *</label>
                  <input style={feltStil} value={f.name} onChange={sett("name")} autoFocus data-testid="vis-kontakt-navn" />
                </div>
                <div>
                  <label style={etikettStil}>Company</label>
                  <input style={feltStil} value={f.company} onChange={sett("company")} data-testid="vis-kontakt-selskap" />
                </div>
              </div>
              <div>
                <label style={etikettStil}>Email *</label>
                <input style={feltStil} type="email" value={f.email} onChange={sett("email")} data-testid="vis-kontakt-epost-felt" />
              </div>
              <div>
                <label style={etikettStil}>Message</label>
                <textarea
                  style={{ ...feltStil, minHeight: 96, resize: "vertical" }}
                  value={f.message}
                  onChange={sett("message")}
                  placeholder="Which role is interesting — and who should we talk to?"
                  data-testid="vis-kontakt-melding"
                />
              </div>
              {feil && (
                <p style={{ fontFamily: mono, fontSize: 11, color: "#e2694f" }} data-testid="vis-kontakt-feil">{feil}</p>
              )}
              <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 4 }}>
                <button type="submit" className="vis-kontakt-btn" disabled={sender || !f.name.trim() || !f.email.trim()} style={{ opacity: sender ? 0.6 : 1 }} data-testid="vis-kontakt-send">
                  {sender ? "Sending …" : "Send"}
                </button>
                <button type="button" className="vis-ctrl" onClick={onLukk} data-testid="vis-kontakt-avbryt">
                  Cancel
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function VisionShare() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const [state, setState] = useState({ status: "laster" });
  const [kode, setKode] = useState("");
  const [feil, setFeil] = useState(false);
  const [sjekker, setSjekker] = useState(false);
  const [visKontakt, setVisKontakt] = useState(false);
  const pdfModus = sp.get("pdf") === "1";

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    document.title = "Norwegian AI Summit 2027 — The Next Shift";
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    const aapne = () => setVisKontakt(true);
    window.addEventListener("vis-kontakt", aapne);
    return () => window.removeEventListener("vis-kontakt", aapne);
  }, []);

  const laasOpp = useCallback(
    async (c) => {
      setSjekker(true);
      setFeil(false);
      try {
        const { data } = await api.post(`/vision/s/${token}/unlock`, { code: c });
        setState({ status: "ok", kode: c.trim().toUpperCase(), ...data });
      } catch (err) {
        if (err?.response?.status === 403) {
          setFeil(true);
          setState((s) => (s.status === "ok" ? s : { status: "laast", view: s.view }));
        } else {
          setState({ status: "borte" });
        }
      } finally {
        setSjekker(false);
      }
    },
    [token]
  );

  useEffect(() => {
    let aktiv = true;
    api
      .get(`/vision/s/${token}`)
      .then(({ data }) => {
        if (!aktiv) return;
        if (data.locked) {
          const qc = sp.get("code");
          if (qc) laasOpp(qc);
          else setState({ status: "laast", view: data.view });
        } else {
          setState({ status: "ok", ...data });
        }
      })
      .catch(() => aktiv && setState({ status: "borte" }));
    return () => {
      aktiv = false;
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.status === "laster") {
    return (
      <div className="vis-root vis-borte" data-testid="vision-share-laster">
        <p style={{ fontFamily: mono, fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase", color: "#8b9199" }}>Loading …</p>
      </div>
    );
  }

  if (state.status === "borte") {
    return (
      <div className="vis-root vis-borte" data-testid="vision-share-borte">
        <p className="vis-brand">
          Norwegian AI Summit <span className="gul">2027</span>
        </p>
        <p style={{ fontFamily: mono, fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8b9199" }}>
          This link is no longer active.
        </p>
        <p style={{ fontSize: 13, color: "#565c66" }}>Please contact the team for an updated link.</p>
      </div>
    );
  }

  if (state.status === "laast") {
    return (
      <div className="vis-root vis-borte" data-testid="vision-gate">
        <p className="vis-brand">
          Norwegian AI Summit <span className="gul">2027</span>
        </p>
        <h1
          style={{ fontFamily: switzer, fontWeight: 500, textTransform: "uppercase", letterSpacing: "-0.02em", fontSize: "clamp(26px, 4vw, 44px)", color: "#f5f1e8" }}
        >
          Private deck.
        </h1>
        <p style={{ fontSize: 13.5, color: "#8b9199" }}>Enter the access code you received with this link.</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (kode.trim()) laasOpp(kode);
          }}
          style={{ display: "flex", gap: 10, marginTop: 6 }}
          data-testid="vision-gate-form"
        >
          <input
            value={kode}
            onChange={(e) => setKode(e.target.value.toUpperCase())}
            placeholder="ACCESS CODE"
            maxLength={12}
            autoFocus
            data-testid="vision-passord-input"
            style={{
              background: "rgba(7, 10, 14, 0.6)",
              border: "1px solid rgba(245, 241, 232, 0.22)",
              color: "#f5f1e8",
              padding: "10px 14px",
              fontFamily: mono,
              fontSize: 16,
              letterSpacing: "0.3em",
              width: 190,
              textTransform: "uppercase",
              outline: "none",
            }}
          />
          <button type="submit" className="vis-ctrl primar" disabled={sjekker || !kode.trim()} data-testid="vision-passord-submit">
            <Lock size={12} /> {sjekker ? "Checking …" : "Unlock"}
          </button>
        </form>
        {feil && (
          <p style={{ fontFamily: mono, fontSize: 11, letterSpacing: "0.06em", color: "#e2694f" }} data-testid="vision-passord-feil">
            Wrong code — please try again.
          </p>
        )}
      </div>
    );
  }

  const pdfHref = `/api/vision/s/${token}/pdf${state.kode ? `?code=${encodeURIComponent(state.kode)}` : ""}`;

  return (
    <div data-testid="vision-share">
      <div className="vis-topbar">
        <span className="vis-brand" style={{ fontSize: 11 }}>
          Norwegian AI Summit <span className="gul">2027</span>
        </span>
        <span className="vis-tag" style={{ marginLeft: "auto" }} data-testid="vision-share-tag">
          {state.view === "speaker" ? "Speaker invitation" : "Partner invitation"} · Private
        </span>
        <button type="button" onClick={() => window.dispatchEvent(new Event("vis-present"))} className="vis-ctrl" data-testid="vision-present-topp">
          <Play size={12} /> Present
        </button>
        {state.view === "partner" && (
          <button type="button" onClick={() => setVisKontakt(true)} className="vis-ctrl" data-testid="vision-kontakt-topp">
            <Mail size={12} /> Contact
          </button>
        )}
        <a className="vis-ctrl primar" style={{ textDecoration: "none" }} href={pdfHref} data-testid="vision-last-ned-pdf">
          <FileDown size={12} /> Download PDF
        </a>
      </div>
      <VisionDeck
        view={state.view}
        data={state.data}
        anbefaling={{ role: state.recommendedRole || "", recipient: state.recipient || "" }}
        kanKontakte={state.view === "partner"}
        pdfModus={pdfModus}
      />
      {visKontakt && <KontaktModal token={token} onLukk={() => setVisKontakt(false)} />}
    </div>
  );
}
